import { clsx } from 'clsx';
import { memo, useCallback, useEffect, useRef } from 'react';

import styles from './SliderProgressBar.css';

export type AnimationDurationScheme = {
  [key: number]: number;
  default: number;
};

export type AnimationDuration = AnimationDurationScheme | number;

type SliderProgressBarProps = {
  /** @description Количество точек */
  count: number;
  /** @description Индекс активной точки */
  activeIndex: number;
  /** @description Длительность анимации */
  duration: AnimationDuration;
  /** @description Приостановить анимацию */
  paused?: boolean;
  /** @description Callback при наведении на прогресс бар */
  onProgressBarHover?: () => void;
  /** @description Callback при выходе с прогресс бара */
  onProgressBarLeave?: () => void;
  /** @description Callback при клике на точку */
  onDotClick?: (index: number) => void;
  /** @description Callback при завершении анимации */
  onAnimationEnd?: (index: number) => void;
};

/**
 * @description Компонент для отображения прогресса слайдера
 */
export const SliderProgressBar = memo<SliderProgressBarProps>(function SliderProgressBar({
  count,
  activeIndex,
  onDotClick,
  onProgressBarHover,
  onProgressBarLeave,
  duration,
  onAnimationEnd,
  paused,
}) {
  const progressRefs = useRef<(HTMLDivElement | null)[]>([]);
  const activeAnimationRef = useRef<Animation | null>(null);

  /**
   * @description Устанавливает анимацию для активной точки
   * @param activeBarIndex - Индекс активной точки
   */
  const setAnimation = useCallback<(activeBarIndex: number) => void>(
    activeBarIndex => {
      // Остановить активную анимацию
      activeAnimationRef.current?.cancel();

      // Сбросить прогресс у всех полосок
      progressRefs.current.forEach((bar, index) => {
        if (bar) {
          // Если индекс меньше активного, заполнить предыдущие, иначе сбросить следующие и активную
          bar.style.width = index < activeBarIndex ? '100%' : '0%';
        }
      });

      // Создать анимацию для активной точки
      const activeBar = progressRefs.current[activeBarIndex];
      if (activeBar) {
        /**
         * @description Определить длительность анимации
         * если duration - число, использовать его
         * если объект схемы - взять значение для текущего индекса или default
         */
        const animationDuration =
          typeof duration === 'number' ? duration : (duration[activeBarIndex] ?? duration.default);

        const animation = activeBar.animate([{ width: '0%' }, { width: '100%' }], {
          duration: animationDuration,
          easing: 'linear',
          fill: 'forwards',
        });

        // Сохранить ссылку на анимацию
        activeAnimationRef.current = animation;

        // Обработчик окончания анимации
        animation.onfinish = (): void => {
          onAnimationEnd?.(activeBarIndex);
        };
      }
    },

    [duration, onAnimationEnd],
  );

  /**
   * @description Приостановить анимацию
   */
  const pauseAnimation = useCallback(() => {
    activeAnimationRef.current?.pause();
  }, []);

  /**
   * @description Возобновить анимацию
   */
  const playAnimation = useCallback(() => {
    activeAnimationRef.current?.play();
  }, []);

  useEffect(() => {
    setAnimation(activeIndex);
  }, [activeIndex, setAnimation]);

  useEffect(() => {
    if (paused) {
      pauseAnimation();
    } else {
      playAnimation();
    }
  }, [paused, pauseAnimation, playAnimation]);

  return (
    <div
      aria-disabled={!onDotClick}
      aria-label="Прогресс слайдера"
      aria-valuemax={count - 1}
      aria-valuemin={0}
      aria-valuenow={activeIndex}
      className={styles['container']}
      data-paused={paused}
      data-testid="slider-progress-bar"
      role="progressbar"
      onMouseEnter={onProgressBarHover}
      onMouseLeave={onProgressBarLeave}
    >
      {Array.from({ length: count }).map((_, idx) => (
        <div
          aria-current={idx === activeIndex}
          className={clsx(
            styles['track-wrapper'],
            onDotClick && styles['track-wrapper--interactive'],
            idx === activeIndex && styles['track-wrapper--active'],
          )}
          data-testid="dot"
          key={idx}
          role="button"
          tabIndex={idx}
          onClick={() => {
            if (!onDotClick) {
              return;
            }

            setAnimation(idx);

            onDotClick(idx);
          }}
        >
          <div className={styles['track']} data-testid="dot-track">
            <div
              className={styles['fill']}
              data-testid="dot-fill"
              ref={el => {
                progressRefs.current[idx] = el;
              }}
            />
          </div>
        </div>
      ))}
    </div>
  );
});
